import axios from "axios";
import getPresignedUrl, { PresignedUrlPayload } from "./postPresignedUrl";

/**
 * 파일 업로드 결과 인터페이스
 */
export interface FileUploadResult {
  fileUrl: string;
  filename: string;
  originalFilename: string;
  contentType: string;
  success: boolean;
}

/**
 * 파일을 업로드하는 유틸리티 함수
 * 1. presigned URL을 발급받음
 * 2. presigned URL로 파일을 직접 업로드
 *
 * @param file - 업로드할 파일 객체
 * @returns 업로드 결과 정보 (파일 URL, 파일명 등)
 */
export const uploadFile = async (file: File): Promise<FileUploadResult> => {
  try {
    // 1. presigned URL 발급 요청
    const payload: PresignedUrlPayload = {
      contentType: file.type,
      originalFilename: file.name,
    };

    const presignedUrlResponse = await getPresignedUrl(payload);

    // success가 false이거나 data가 없으면 에러
    if (presignedUrlResponse.success === false || !presignedUrlResponse.data) {
      throw new Error(
        presignedUrlResponse.message || "Failed to get presigned URL",
      );
    }

    const { presignedUrl, fileUrl, filename } = presignedUrlResponse.data;

    // 2. presigned URL을 통해 실제 파일 업로드
    const uploadResult = await axios.put(presignedUrl, file, {
      headers: {
        "Content-Type": file.type,
      },
    });

    return {
      fileUrl,
      filename,
      originalFilename: file.name,
      contentType: file.type,
      success: true,
    };
  } catch (error) {
    return {
      fileUrl: "",
      filename: "",
      originalFilename: file.name,
      contentType: file.type,
      success: false,
    };
  }
};

/**
 * 다중 파일 업로드 유틸리티 함수
 *
 * @param files - 업로드할 파일 배열 또는 FileList 객체
 * @returns 업로드 결과 배열
 */
export const uploadMultipleFiles = async (
  files: File[] | FileList,
): Promise<FileUploadResult[]> => {
  const fileArray = Array.from(files);
  const uploadPromises = fileArray.map((file) => uploadFile(file));

  return Promise.all(uploadPromises);
};

/**
 * Base64 문자열을 File 객체로 변환 후 업로드하는 함수
 *
 * @param base64 - Base64 인코딩된 파일 데이터 (data:image/jpeg;base64,...)
 * @param filename - 파일명
 * @param contentType - 파일 타입 (기본값: image/jpeg)
 * @returns 업로드 결과
 */
export const uploadBase64File = async (
  base64: string,
  filename: string,
  contentType = "image/jpeg",
): Promise<FileUploadResult> => {
  try {
    // Base64 데이터에서 헤더 부분 제거
    const base64Data = base64.split(",")[1] || base64;

    // Base64를 Blob으로 변환
    const byteCharacters = atob(base64Data);
    const byteArrays = [];

    for (let i = 0; i < byteCharacters.length; i += 512) {
      const slice = byteCharacters.slice(i, i + 512);
      const byteNumbers = new Array(slice.length);

      for (let j = 0; j < slice.length; j++) {
        byteNumbers[j] = slice.charCodeAt(j);
      }

      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }

    const blob = new Blob(byteArrays, { type: contentType });
    const file = new File([blob], filename, { type: contentType });

    // 파일 업로드
    return uploadFile(file);
  } catch (error) {
    console.error("Base64 file upload failed:", error);

    return {
      fileUrl: "",
      filename: "",
      originalFilename: filename,
      contentType,
      success: false,
    };
  }
};
